import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { NeuButton } from "../../components/neu/Neu";
import { FormPageShell } from "../../components/data/FormPageShell";
import { BuilderHead } from "./BuilderHead";
import { EMPTY_LINE, PurchaseLineEditor } from "./PurchaseLineEditor";
import { BuilderTotalsPack } from "./BuilderTotalsPack";
import { buildHeader } from "./headerForApi";
import { useBranchScope } from "./branchScope";
import { validationHints } from "./validationHints";
import { createPurchaseDoc } from "../../api/purchases";
import { fmtMoney } from "../../lib/format";

const today = () => new Date().toISOString().slice(0, 10);

const initialHead = (scopeBranchId = "") => ({
  supplier_id: "", date: today(), needed_by: today(), currency: "AED",
  payment_terms_kind: "cash", payment_terms_days: 0,
  delivery_branch_id: scopeBranchId || "", delivery_warehouse_id: "",
  branch_id: scopeBranchId || null,
  discount_percent: "", discount_amount: "",
  tax_percent: "", tax_amount: "", tax_default_pct: null, notes: "",
  warranty_months: "", warranty_provider: "", warranty_notes: "",
});
// إنشاء طلب/أمر شراء بصفحة كاملة: رأس + بنود + إجماليات + كفالة + دفعات.
export const PurchaseBuilder = ({ docType, onCreated, onCancel }) => {
  const { t } = useTranslation();
  const { branchId: scopeBranchId } = useBranchScope();
  const [head, setHead] = useState(() => initialHead(scopeBranchId));
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [plan, setPlan] = useState([{ kind: "advance", percent: "", days: "", notes: "" }]);
  const [busy, setBusy] = useState(false);
  const needsSupplier = docType !== "requisition";
  const currency = head.currency || "AED";
  const check = validationHints(t, head, lines, needsSupplier);

  // إذا تغيّر نطاق الفرع العام (فلتر أعلى المشتريات) اسحبه إلى النموذج فورًا.
  useEffect(() => {
    if (scopeBranchId && scopeBranchId !== head.delivery_branch_id) {
      setHead((h) => ({ ...h, delivery_branch_id: scopeBranchId,
        branch_id: scopeBranchId, delivery_warehouse_id: null }));
    }
  }, [scopeBranchId]);

  const save = async () => {
    setBusy(true);
    try {
      const taxCode = head.tax_percent !== "" && Number(head.tax_percent) === 0
        ? "ae_out_of_scope" : "ae_standard";
      const doc = await createPurchaseDoc({
        doc_type: docType, header: buildHeader(head, plan, today()),
        lines: lines.map((line, index) => ({ ...line, line_no: index + 1, tax_code_key: taxCode })),
      });
      toast.success(`${t(`purchases.created.${docType}`)} — ${fmtMoney(doc.totals.payable)} ${doc.money.currency}`);
      onCreated?.();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "price_variance_reason_required") {
        toast.error(`${t("purchases.priceVarianceBlocked")} (${detail.lines?.[0]?.reference_price} → ${detail.lines?.[0]?.new_price})`);
      } else if (detail?.code === "master_item_required") {
        toast.error(t("purchases.items.pickFromMaster"));
      } else {
        toast.error(detail?.code || t("errors.generic"));
      }
    } finally { setBusy(false); }
  };

  return (
    <FormPageShell testid="purchase-builder"
      title={t(`purchases.new.${docType}`)} subtitle={t("purchases.title")}
      onBack={onCancel} onCancel={onCancel} onSubmit={save}
      submitLabel={t("purchases.checkAndSave")} busy={busy} disabled={!check.valid}
      hint={!check.valid ? `${t("purchases.validate.missing")}: ${check.missing.join(" · ")}` : null}>
      <BuilderHead head={head} onChange={setHead} needsSupplier={needsSupplier} />
      <div className="grid gap-2">
        <p className="text-xs font-bold">{t("purchases.linesTitle")}</p>
        {lines.map((line, index) => (
          <PurchaseLineEditor key={index} line={line} index={index} currency={currency}
                              onChange={(i, next) => setLines(lines.map((l, x) => (x === i ? next : l)))}
                              onRemove={(i) => setLines(lines.filter((_, x) => x !== i))} />
        ))}
        <NeuButton className="w-fit px-3 py-2 text-xs" data-testid="purchase-add-line"
                   onClick={() => setLines([...lines, { ...EMPTY_LINE }])}>
          <Plus className="h-3.5 w-3.5" /> {t("purchases.addLine")}
        </NeuButton>
      </div>
      {needsSupplier ? (
        <BuilderTotalsPack lines={lines} head={head} onChange={setHead}
                           plan={plan} onPlan={setPlan} currency={currency} />
      ) : null}
    </FormPageShell>
  );
};
